import { AppColors } from 'common/src/types';
import { createTheme } from '@mui/material/styles';

export const MuiTheme = (colors: AppColors) => {
    const borderRadius = '16px';

    const theme = createTheme({
        palette: {
            mode: 'light',
            primary: {
                main: colors.primary,
                dark: colors.primary,
                contrastText: colors.white,
                light: colors.primary + '80',
            },
            secondary: {
                main: colors.star,
                dark: colors.star,
                contrastText: colors.black,
                light: colors.star + '80',
            },
            error: {
                main: colors.danger,
                dark: colors.danger,
                contrastText: colors.white,
                light: colors.danger + '80',
            },
            warning: {
                main: colors.star,
                dark: colors.star,
                contrastText: colors.black,
                light: colors.star + '80',
            },
            info: {
                main: colors.waterColor,
                dark: colors.waterColor,
                contrastText: colors.black,
                light: colors.waterColor + '80',
            },
            success: {
                main: colors.success,
                dark: colors.success,
                contrastText: colors.black,
                light: colors.success + '80',
            },
            text: {
                primary: colors.text + "!Important",
                disabled: colors.text + '40 !Important',
                secondary: colors.text + '80 !Important',
            },
            background: {
                paper: colors.card,
                default: colors.background,
            },
            divider: colors.border,
            action: {
                hover: colors.primary + '20',
                active: colors.primary + '80',
                selected: colors.primary + '30',
                disabled: colors.primary + '40',
                disabledBackground: colors.primary + '20',
            },
            common: {
                black: colors.black,
                white: colors.white,
            },
            grey: {
                50: colors.background,
                100: colors.card,
                200: colors.border,
                300: colors.placeholder,
                400: colors.text + '80',
                500: colors.text,
                600: colors.text,
                700: colors.black + '80',
                800: colors.black,
                900: colors.black,
            },
        },
        typography: {
            allVariants: {
                color: colors.text,
            },
            h1: {
                '@media (max-width: 600px)': {
                    fontSize: '2.5rem',
                },
            },
            h2: {
                '@media (max-width: 600px)': {
                    fontSize: '2rem',
                },
            },
            h3: {
                '@media (max-width: 600px)': {
                    fontSize: '1.75rem',
                },
            },
            h4: {
                '@media (max-width: 600px)': {
                    fontSize: '1.5rem',
                },
            },
            h5: {
                '@media (max-width: 600px)': {
                    fontSize: '1.25rem',
                },
            },
            h6: {
                '@media (max-width: 600px)': {
                    fontSize: '1.1rem',
                },
            },
            subtitle1: {
                '@media (max-width: 600px)': {
                    fontSize: '0.95rem',
                },
            },
            subtitle2: {
                '@media (max-width: 600px)': {
                    fontSize: '0.85rem',
                },
            },
            body1: {
                '@media (max-width: 600px)': {
                    fontSize: '0.9rem',
                },
            },
            body2: {
                '@media (max-width: 600px)': {
                    fontSize: '0.8rem',
                },
            },
            button: {
                '@media (max-width: 600px)': {
                    fontSize: '0.8rem',
                },
            },
            caption: {
                '@media (max-width: 600px)': {
                    fontSize: '0.7rem',
                },
            },
            overline: {
                '@media (max-width: 600px)': {
                    fontSize: '0.65rem',
                },
            },
        },
        components: {
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        '& .MuiSwitch-switchBase': {
                            color: colors.text + '60',
                            '&:hover': {
                                backgroundColor: colors.primary + '14',
                            },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: colors.primary,
                            '&:hover': {
                                backgroundColor: colors.primary + '14',
                            },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: colors.primary,
                        },
                        '& .MuiSwitch-track': {
                            backgroundColor: colors.text + '30',
                        },
                    },
                    thumb: {
                        boxShadow: `0 1px 3px ${colors.primary}`,
                    },
                    track: {
                        borderRadius: 16,
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: {
                    '*': {
                        scrollbarWidth: 'thin',
                        scrollbarColor: `${colors.text} ${colors.card}`,
                    },
                    'input:-webkit-autofill': {
                        caretColor: `${colors.text} !important`,
                        WebkitTextFillColor: `${colors.text} !important`,
                        transition: 'background-color 5000s ease-in-out 0s',
                        WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                    },
                    'input:-webkit-autofill:hover': {
                        caretColor: `${colors.text} !important`,
                        WebkitTextFillColor: `${colors.text} !important`,
                        WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                    },
                    'input:-webkit-autofill:focus': {
                        caretColor: `${colors.text} !important`,
                        WebkitTextFillColor: `${colors.text} !important`,
                        WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                    },
                    'input:-webkit-autofill:active': {
                        caretColor: `${colors.text} !important`,
                        WebkitTextFillColor: `${colors.text} !important`,
                        WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                    },
                    'input:-moz-autofill': {
                        color: `${colors.text} !important`,
                        background: `${colors.card} !important`,
                    },
                    '& input[type=number]': {
                        MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                    },
                    'input:-moz-autofill:hover': {
                        background: `${colors.card} !important`,
                        color: `${colors.text} !important`,
                    },
                    'input:-moz-autofill:focus': {
                        background: `${colors.card} !important`,
                        color: `${colors.text} !important`,
                    },
                    'input:autofill': {
                        background: `${colors.card} !important`,
                        color: `${colors.text} !important`,
                    },
                    '.sketch-picker > :nth-of-type(3)': {
                        display: 'none !Important',
                    },
                    '.Component-horizontalScrollContainer-12': {
                        backgroundColor: colors.card,
                        color: colors.text,
                        '&::-webkit-scrollbar': {
                            height: 8,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: colors.text,
                            borderRadius: 8,
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: colors.card,
                        },
                        scrollbarColor: `${colors.text} ${colors.card}`,
                        scrollbarWidth: 'thin',
                    },
                    '.MuiTableHead-root': {
                        backgroundColor: colors.card,
                    },
                    '.MuiTableHead-root th': {
                        color: colors.text,
                    },
                    '.MuiTableSortLabel-iconDirectionDesc': {
                        backgroundColor: colors.card,
                        color: colors.text,
                    },
                    '.MuiTableCell-head': {
                        backgroundColor: colors.card,
                        color: colors.text,
                    },
                    '.MuiTableSortLabel-root': {
                        color: colors.text,
                    },
                    '.MuiTableSortLabel-root.Mui-active': {
                        color: colors.text,
                    },
                    '.MuiTableSortLabel-icon': {
                        color: colors.text,
                    },
                    '.MuiPaper-root .MuiTableCell-head': {
                        color: colors.text,
                        backgroundColor: colors.card,
                    },
                },
            },
            MuiSnackbar: {
                styleOverrides: {
                    root: {
                        '& .MuiSnackbarContent-root': {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            padding: 0,
                            minWidth: 'auto',
                        },
                    },
                    anchorOriginTopCenter: {
                        top: '16px',
                        transform: 'translateX(-50%)',
                        left: '50%',
                        right: 'auto',
                    },
                    anchorOriginTopLeft: {
                        top: '16px',
                        left: '16px',
                    },
                    anchorOriginTopRight: {
                        top: '16px',
                        right: '16px',
                    },
                    anchorOriginBottomCenter: {
                        bottom: '16px',
                        transform: 'translateX(-50%)',
                        left: '50%',
                        right: 'auto',
                    },
                    anchorOriginBottomLeft: {
                        bottom: '16px',
                        left: '16px',
                    },
                    anchorOriginBottomRight: {
                        bottom: '16px',
                        right: '16px',
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        backgroundColor: colors.card,
                        border: `1px solid ${colors.border}`,
                        '& .MuiChip-deleteIcon': {
                            color: colors.text,
                            fontSize: '18px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                color: colors.primary,
                                transform: 'scale(1.1)',
                            },
                            '&:active': {
                                transform: 'scale(0.95)',
                            },
                        },
                        '& .MuiChip-icon': {
                            color: colors.text,
                            fontSize: '18px',
                        },
                        '& .MuiChip-avatar': {
                            color: colors.text,
                            backgroundColor: colors.primary + '20',
                        },
                        '&:hover': {
                            backgroundColor: colors.primary + '10',
                            borderColor: colors.primary + '40',
                        },
                        '&.MuiChip-clickable': {
                            '&:hover': {
                                backgroundColor: colors.primary + '15',
                            },
                            '&:active': {
                                backgroundColor: colors.primary + '25',
                            },
                        },
                        '&.MuiChip-outlined': {
                            borderColor: colors.border,
                            '&:hover': {
                                borderColor: colors.primary,
                                backgroundColor: colors.primary + '10',
                            },
                        },
                        '&.Mui-disabled': {
                            opacity: 0.5,
                            '& .MuiChip-deleteIcon': {
                                color: colors.text + '60',
                                cursor: 'not-allowed',
                            },
                        },
                    },
                    label: {
                        color: colors.text,
                        fontWeight: 500,
                    },
                    deleteIcon: {
                        color: colors.text,
                        '&:hover': {
                            color: colors.primary,
                        },
                    },
                    icon: {
                        color: colors.text,
                    },
                },
            },
            MuiSlider: {
                styleOverrides: {
                    root: {
                        color: colors.primary,
                        height: 2,
                        padding: '16px 0',
                        '&:hover': {
                            '& .MuiSlider-thumb': {
                                boxShadow: `0 0 0 8px ${colors.primary}20`,
                            },
                        },
                        '&.Mui-disabled': {
                            opacity: 0.5,
                            '& .MuiSlider-thumb': {
                                backgroundColor: colors.card,
                                borderColor: `${colors.text}60`,
                            },
                            '& .MuiSlider-track': {
                                backgroundColor: `${colors.text}60`,
                            },
                            '& .MuiSlider-rail': {
                                backgroundColor: colors.border,
                            },
                        },
                    },
                    thumb: {
                        height: 18,
                        width: 18,
                        backgroundColor: colors.card,
                        border: `2px solid ${colors.primary}`,
                        '&:hover, &.Mui-focusVisible': {
                            boxShadow: `0 0 0 8px ${colors.primary}20`,
                        },
                        '&.Mui-active': {
                            boxShadow: `0 0 0 12px ${colors.primary}20`,
                        },
                        '&:before': {
                            boxShadow: 'none',
                        },
                    },
                    track: {
                        height: 2,
                        borderRadius: 1,
                        backgroundColor: colors.primary,
                    },
                    rail: {
                        height: 2,
                        borderRadius: 1,
                        backgroundColor: colors.border,
                        opacity: 1,
                    },
                    mark: {
                        backgroundColor: colors.border,
                        height: 6,
                        width: 1,
                        marginTop: -2,
                        '&.MuiSlider-markActive': {
                            backgroundColor: colors.primary,
                            opacity: 0.8,
                        },
                    },
                    markLabel: {
                        color: `${colors.text}80`,
                        fontSize: '0.7rem',
                        marginTop: 8,
                        '&.MuiSlider-markLabelActive': {
                            color: colors.primary,
                        },
                    },
                    valueLabel: {
                        backgroundColor: colors.primary,
                        color: colors.white,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        padding: '2px 6px',
                        borderRadius: '4px',
                        '&:before': {
                            display: 'none',
                        },
                        '& .MuiSlider-valueLabelLabel': {
                            color: colors.white,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                        },
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        color: colors.text + "!important",
                        transition: 'all 0.2s ease' + "!important",
                        backgroundColor: colors.card + "!important",


                        border: `1px solid ${colors.border} !important`,

                        '&:hover': {
                            border: `0px solid ${colors.transparent} !important`,
                            backgroundColor: colors.primary + '10' + "!important",
                        },

                        '&.Mui-selected': {
                            color: colors.white + "!important",
                            borderColor: colors.primary + "!important",
                            backgroundColor: colors.primary + "!important",
                            border: `1px solid ${colors.primary} !important`,

                            '&:hover': {
                                border: `1px solid ${colors.primary} !important`,
                                backgroundColor: colors.primary + 'DD' + "!important",
                            },
                        },

                        '&.Mui-disabled': {
                            opacity: 0.5,
                        }
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: '12px',
                        padding: '10px 16px',
                        fontWeight: 500,
                        backgroundColor: colors.card,
                        color: colors.text,
                        borderLeft: `4px solid ${colors.primary}`,
                        boxShadow: `0 4px 12px ${colors.shadow}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '280px',
                        maxWidth: '90vw',
                        '& .MuiAlert-icon': {
                            color: colors.primary,
                            fontSize: '22px',
                            padding: 0,
                            marginRight: '8px',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiAlert-message': {
                            color: colors.text,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            padding: 0,
                            flex: 1,
                            lineHeight: 1.4,
                        },
                        '& .MuiAlert-action': {
                            padding: 0,
                            marginRight: '-4px',
                            marginLeft: '8px',
                            alignItems: 'center',
                            '& .MuiIconButton-root': {
                                padding: '4px',
                                color: colors.text,
                                opacity: 0.7,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    color: colors.primary,
                                    opacity: 1,
                                    backgroundColor: 'transparent',
                                    transform: 'scale(1.05)',
                                },
                                '&:active': {
                                    transform: 'scale(0.95)',
                                },
                                '& svg': {
                                    fontSize: '18px',
                                },
                            },
                        },
                    },
                    standardSuccess: {
                        borderLeftColor: colors.success,
                        '& .MuiAlert-icon': {
                            color: colors.success,
                        },
                    },
                    standardError: {
                        borderLeftColor: colors.danger,
                        '& .MuiAlert-icon': {
                            color: colors.danger,
                        },
                    },
                    standardWarning: {
                        borderLeftColor: colors.star,
                        '& .MuiAlert-icon': {
                            color: colors.star,
                        },
                    },
                    standardInfo: {
                        borderLeftColor: colors.waterColor,
                        '& .MuiAlert-icon': {
                            color: colors.waterColor,
                        },
                    },
                    filledSuccess: {
                        backgroundColor: colors.success,
                        color: colors.white,
                        borderLeftColor: colors.success,
                        '& .MuiAlert-icon': {
                            color: colors.white,
                        },
                        '& .MuiAlert-message': {
                            color: colors.white,
                        },
                        '& .MuiAlert-action .MuiIconButton-root': {
                            color: colors.white,
                            '&:hover': {
                                color: colors.white,
                                opacity: 0.8,
                            },
                        },
                    },
                    filledError: {
                        backgroundColor: colors.danger,
                        color: colors.white,
                        borderLeftColor: colors.danger,
                        '& .MuiAlert-icon': {
                            color: colors.white,
                        },
                        '& .MuiAlert-message': {
                            color: colors.white,
                        },
                        '& .MuiAlert-action .MuiIconButton-root': {
                            color: colors.white,
                            '&:hover': {
                                color: colors.white,
                                opacity: 0.8,
                            },
                        },
                    },
                    filledWarning: {
                        backgroundColor: colors.star,
                        color: colors.black,
                        borderLeftColor: colors.star,
                        '& .MuiAlert-icon': {
                            color: colors.black,
                        },
                        '& .MuiAlert-message': {
                            color: colors.black,
                        },
                        '& .MuiAlert-action .MuiIconButton-root': {
                            color: colors.black,
                            '&:hover': {
                                color: colors.black,
                                opacity: 0.8,
                            },
                        },
                    },
                    filledInfo: {
                        backgroundColor: colors.waterColor,
                        color: colors.white,
                        borderLeftColor: colors.waterColor,
                        '& .MuiAlert-icon': {
                            color: colors.white,
                        },
                        '& .MuiAlert-message': {
                            color: colors.white,
                        },
                        '& .MuiAlert-action .MuiIconButton-root': {
                            color: colors.white,
                            '&:hover': {
                                color: colors.white,
                                opacity: 0.8,
                            },
                        },
                    },
                    outlined: {
                        backgroundColor: 'transparent',
                        border: `1px solid ${colors.border}`,
                        color: colors.text,
                        boxShadow: 'none',
                    },
                    outlinedSuccess: {
                        borderColor: colors.success,
                        '& .MuiAlert-icon': {
                            color: colors.success,
                        },
                    },
                    outlinedError: {
                        borderColor: colors.danger,
                        '& .MuiAlert-icon': {
                            color: colors.danger,
                        },
                    },
                    outlinedWarning: {
                        borderColor: colors.star,
                        '& .MuiAlert-icon': {
                            color: colors.star,
                        },
                    },
                    outlinedInfo: {
                        borderColor: colors.waterColor,
                        '& .MuiAlert-icon': {
                            color: colors.waterColor,
                        },
                    },
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                    },
                },
            },
            MuiToolbar: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.card,
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: colors.card,
                        color: colors.text,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        padding: '8px 12px',
                        borderRadius: '8px',
                        boxShadow: `0 4px 12px ${colors.shadow}`,
                        border: `1px solid ${colors.border}`,
                        maxWidth: '300px',
                        lineHeight: 1.4,
                        backdropFilter: 'blur(4px)',
                        background: `linear-gradient(135deg, ${colors.card} 0%, ${colors.card}EE 100%)`,
                    },
                    arrow: {
                        color: colors.card,
                        '&:before': {
                            border: `1px solid ${colors.border}`,
                            backgroundColor: colors.card,
                            boxShadow: `0 2px 4px ${colors.shadow}`,
                        },
                    },
                    popper: {
                        opacity: 0.95,
                        transition: 'opacity 0.2s ease',
                        '&[data-popper-placement*="top"] .MuiTooltip-arrow': {
                            marginBottom: '-1px',
                        },
                        '&[data-popper-placement*="bottom"] .MuiTooltip-arrow': {
                            marginTop: '-1px',
                        },
                        '&[data-popper-placement*="left"] .MuiTooltip-arrow': {
                            marginRight: '-1px',
                        },
                        '&[data-popper-placement*="right"] .MuiTooltip-arrow': {
                            marginLeft: '-1px',
                        },
                    },
                    tooltipPlacementTop: {
                        marginBottom: '8px',
                    },
                    tooltipPlacementBottom: {
                        marginTop: '8px',
                    },
                    tooltipPlacementLeft: {
                        marginRight: '8px',
                    },
                    tooltipPlacementRight: {
                        marginLeft: '8px',
                    },
                    // tooltipTouch: {
                    //     padding: '10px 14px',
                    //     fontSize: '0.875rem',
                    // },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: colors?.card,

                            "& fieldset": {
                                borderWidth: "0 !Important",
                            },
                            "&.Mui-disabled": {
                                opacity: 0.5,
                                WebkitTextFillColor: `${colors?.text} !important`,
                            },
                        },
                        "& .MuiInputBase-input": {
                            fontWeight: 500,
                            fontSize: "16px",
                            color: colors?.text + "!Important",
                            fontFamily: "'Roboto', sans-serif",
                            "&::placeholder": {
                                color: colors?.placeholder,
                                opacity: 1,
                            },
                            "&.Mui-disabled": {
                                WebkitTextFillColor: `${colors?.text} !important`,
                                color: `${colors?.text} !important`,
                                opacity: 0.5,
                            },
                        },
                    }
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.card,
                        '& th': {
                            color: colors.text,
                        },
                    },
                },
            },
            MuiStepConnector: {
                styleOverrides: {
                    line: {
                        borderColor: colors.text + '40',
                    },
                },
            },
            MuiStepIcon: {
                styleOverrides: {
                    root: {
                        color: colors.text + '40',
                        '&.Mui-active': {
                            color: colors.primary,
                        },
                        '&.Mui-completed': {
                            color: colors.primary,
                        },
                        '& text': {
                            fill: colors.card,
                        },
                    },
                },
            },
            MuiStepLabel: {
                styleOverrides: {
                    root: {
                        color: colors.text + '80',
                        '&.Mui-active': {
                            color: colors.text,
                            fontWeight: 'bold',
                        },
                        '&.Mui-completed': {
                            color: colors.primary,
                        },
                    },
                    iconContainer: {
                        paddingRight: 8,
                    },
                    label: {
                        '&.Mui-active': {
                            color: colors.text,
                        },
                        '&.Mui-completed': {
                            color: colors.primary,
                        },
                    },
                },
            },
            MuiStepper: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'transparent',
                        padding: '24px 0',
                    },
                },
            },
            MuiStep: {
                styleOverrides: {
                    root: {
                        '&.Mui-completed': {
                            color: colors.primary,
                        },
                    },
                },
            },
            MuiTableBody: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.card,
                        '& td': {
                            color: colors.text,
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        padding: '5px',
                        boxShadow: 'none !important',
                        backgroundColor: colors.card,
                    },
                },
            },
            MuiTableContainer: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        scrollbarWidth: 'thin',
                        backgroundColor: colors.card,
                        scrollbarColor: `${colors.card} transparent`,
                        '&::-webkit-scrollbar': {
                            height: 8,
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: colors.primary,
                            borderRadius: 8,
                        },
                    },
                },
            },
            MuiGrid: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'transparent !important',
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.card,
                        '&:nth-of-type(odd)': {
                            backgroundColor: colors.card,
                        },
                        '&:nth-of-type(even)': {
                            backgroundColor: colors.card,
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        borderColor: colors.card,
                        backgroundColor: colors.card,
                        '& a': {
                            color: colors.text,
                        },
                        '& svg': {
                            color: colors.text,
                        },
                        '& .MuiTypography-root': {
                            color: colors.text,
                        },
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        marginRight: '16px',
                        '&.Mui-selected': {
                            color: colors.primary,
                            fontWeight: 'bold',
                        },
                        '&.Mui-focusVisible': {
                            outline: `2px solid ${colors.primary}`,
                            outlineOffset: '2px',
                        },
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        color: `${colors.text} !important`,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${colors.primary + '40'} !important`,
                            borderWidth: `1px !important`,
                            transition: 'all 0.2s ease !important',
                        },

                        '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.primary} !important`,
                                borderWidth: `1px !important`,
                            },
                            '& .MuiSelect-icon': {
                                color: `${colors.primary} !important`,
                            },
                        },

                        '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.primary} !important`,
                                borderWidth: `2px !important`,
                                boxShadow: `0 0 0 2px ${colors.primary}20 !important`,
                            },
                            '& .MuiSelect-icon': {
                                color: `${colors.primary} !important`,
                            },
                        },

                        '&:focus-visible': {
                            outline: `none !important`,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.primary} !important`,
                                borderWidth: `2px !important`,
                                boxShadow: `0 0 0 3px ${colors.primary}40 !important`,
                            },
                            '& .MuiSelect-icon': {
                                color: `${colors.primary} !important`,
                            },
                        },

                        '& .MuiSelect-select': {
                            '&:focus-visible': {
                                outline: `none !important`,
                                backgroundColor: 'transparent !important',
                                '& + .MuiOutlinedInput-notchedOutline': {
                                    borderColor: `${colors.primary} !important`,
                                    borderWidth: `2px !important`,
                                    boxShadow: `0 0 0 3px ${colors.primary}40 !important`,
                                },
                            },
                        },

                        '& .MuiSelect-icon': {
                            color: `${colors.primary + '80'} !important`,
                            transition: 'color 0.2s ease !important',

                            '&.MuiSelect-iconOpen': {
                                color: `${colors.primary} !important`,
                                transform: 'rotate(180deg) !important',
                            },
                        },

                        '&.Mui-disabled': {
                            opacity: '0.5 !important',
                            '& .MuiSelect-icon': {
                                color: `${colors.primary + '40'} !important`,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.primary + '20'} !important`,
                            },
                        },
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        marginTop: '8px !important',
                        borderRadius: '8px !important',
                        backgroundImage: 'none !important',
                        backgroundColor: `${colors.card} !important`,
                        boxShadow: `0 8px 24px ${colors.shadow} !important`,
                    },
                },
            },

            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        margin: '2px 0 !important',
                        padding: '10px 16px !important',
                        borderRadius: '6px !important',
                        color: `${colors.text} !important`,
                        transition: 'all 0.2s ease !important',

                        '&:hover': {
                            backgroundColor: `${colors.primary + '20'} !important`,
                            color: `${colors.primary} !important`,
                        },

                        '&.Mui-selected': {
                            backgroundColor: `${colors.primary + '30'} !important`,
                            color: `${colors.primary} !important`,

                            '&:hover': {
                                backgroundColor: `${colors.primary + '40'} !important`,
                            },
                        },

                        '&.Mui-focusVisible': {
                            backgroundColor: `${colors.primary + '20'} !important`,
                            outline: `2px solid ${colors.primary} !important`,
                            outlineOffset: '-2px !important',
                        },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        backgroundColor: colors.primary,
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                    },
                    input: {
                        color: colors.text,
                    },
                },
            },
            MuiInput: {
                styleOverrides: {
                    root: {
                        '&:before': {
                            borderBottom: `1px solid ${colors.text}`,
                        },
                        '&:hover:not(.Mui-disabled):before': {
                            borderBottom: `1px solid ${colors.text}`,
                        },
                        '&.Mui-focused:after': {
                            borderBottom: `1px solid ${colors.primary}`,
                        },
                    },
                },
            },
            MuiContainer: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'red',
                        background: `linear-gradient(135deg, ${colors.card} 0%, ${colors.card}CC 100%)`,
                    }
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        '&.Mui-focused': {
                            color: colors.primary,
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                    },
                },
            },
            MuiCardActions: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        display: 'flex',
                        transition: 'ease',
                        gap: theme.spacing(2),
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }),
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        gap: 10,
                        width: '100%',
                        height: 'auto',
                        margin: 'auto',
                        '@media (min-width:600px)': {
                            maxWidth: 400,
                        },
                        '@media (min-width:900px)': {
                            maxWidth: 600,
                        },
                        padding: '24px',
                        transition: 'ease',
                        borderRadius,
                        boxSizing: 'border-box',
                        backdropFilter: 'blur(5px)',
                        boxShadow: `0 4px 20px ${colors.shadow}`,
                        background: `linear-gradient(135deg, ${colors.card} 0%, ${colors.card}CC 100%)`,
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.text,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.text,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.primary,
                        },
                    },
                    input: {
                        color: colors.text,
                    },
                },
            },
            MuiModal: {
                styleOverrides: {
                    root: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        gap: 10,
                        borderRadius,
                        width: '100%',
                        height: 'auto',
                        margin: 'auto',
                        maxWidth: '90%',
                        padding: '24px',
                        overflow: 'hidden',
                        transition: 'ease',
                        boxSizing: 'border-box',
                        backdropFilter: 'blur(5px)',
                        boxShadow: `0 4px 20px ${colors.shadow}`,
                        background: `linear-gradient(135deg, ${colors.card} 0%, ${colors.card}CC 100%)`,
                        '@media (min-width:600px)': {
                            maxWidth: 400,
                        },
                        '@media (min-width:900px)': {
                            maxWidth: 600,
                        },
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontWeight: 'bold',
                        textAlign: 'center',
                        color: colors.text,
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        '&:hover': {
                            color: colors.text,
                            backgroundColor: 'transparent',
                        },
                        '&.Mui-disabled': {
                            color: `${colors.text}66`,
                            backgroundColor: 'transparent',
                        },
                        '&:active': {
                            color: colors.primary
                        },
                    },
                },
            },
            MuiInputAdornment: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                    },
                    positionStart: {
                        color: colors.text,
                    },
                    positionEnd: {
                        color: colors.text,
                    },
                },
            },
            MuiDialogContentText: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        textAlign: 'center',
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        justifyContent: 'center',
                    },
                },
            },
            MuiBackdrop: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    },
                },
            },
            MuiTableSortLabel: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        '&.Mui-active': {
                            color: colors.text,
                        },
                        '&:hover': {
                            color: colors.text,
                        },
                        '& .MuiTableSortLabel-icon': {
                            color: colors.text,
                        },
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease',
                        '&.Mui-disabled': {
                            backgroundColor: `${colors.text}20 !important`,
                            color: `${colors.text}60 !important`,
                            borderColor: `${colors.text}20 !important`,
                            cursor: 'not-allowed',
                            pointerEvents: 'all !important',
                            '&:hover': {
                                backgroundColor: `${colors.text}20 !important`,
                            },
                            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                                color: `${colors.text}60 !important`,
                                '& svg': {
                                    color: `${colors.text}60 !important`,
                                },
                            },
                        },
                    },
                    contained: {
                        backgroundColor: colors.primary,
                        color: colors.white,
                        '&:hover': {
                            backgroundColor: colors.primary + 'DD',
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${colors.primary}40`,
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: `${colors.text}20 !important`,
                            color: `${colors.text}60 !important`,
                            boxShadow: 'none',
                            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                                color: `${colors.text}60 !important`,
                                '& svg': {
                                    color: `${colors.text}60 !important`,
                                },
                            },
                        },
                        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                            color: colors.white,
                            '& svg': {
                                color: colors.white,
                            },
                        },
                    },
                    outlined: {
                        color: colors.primary,
                        borderColor: colors.primary,
                        '&:hover': {
                            backgroundColor: colors.primary + '15',
                            borderColor: colors.primary,
                        },
                        '&.Mui-disabled': {
                            borderColor: `${colors.text}20 !important`,
                            color: `${colors.text}60 !important`,
                            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                                color: `${colors.text}60 !important`,
                                '& svg': {
                                    color: `${colors.text}60 !important`,
                                },
                            },
                        },
                        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                            color: colors.primary,
                            '& svg': {
                                color: colors.primary,
                            },
                        },
                    },
                    text: {
                        color: colors.text,
                        '&:hover': {
                            backgroundColor: colors.primary + '15',
                        },
                        '&.Mui-disabled': {
                            color: `${colors.text}60 !important`,
                            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                                color: `${colors.text}60 !important`,
                                '& svg': {
                                    color: `${colors.text}60 !important`,
                                },
                            },
                        },
                        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                            color: colors.text,
                            '& svg': {
                                color: colors.text,
                            },
                        },
                    },
                },
            },
        },
    });

    Object.entries(colors).forEach(([key, value]) => {
        (theme as any)[key] = value;
    });

    return theme;
};